import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EstablishmentContext = createContext(null);
const STORE_KEY = "findo_est_v1";

export function EstablishmentProvider({ children }) {
  const { partner } = useAuth();
  const [establishments, setEstablishments] = useState([]);
  const [selectedId, setSelectedId] = useState(() => {
    try { return localStorage.getItem(STORE_KEY) || "all"; } catch { return "all"; }
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const list = await api("/establishments");
      const arr = Array.isArray(list) ? list : [];
      setEstablishments(arr);
      // Staff are locked to a single establishment: force-select it so the whole
      // dashboard (badge, notifications, lists) reflects THEIR site, never the
      // primary account (Bug B).
      const locked = partner?.role === "staff" && arr.length >= 1;
      if (locked) {
        if (selectedId !== arr[0].id) {
          setSelectedId(arr[0].id);
          try { localStorage.setItem(STORE_KEY, arr[0].id); } catch { /* ignore */ }
        }
      } else if (selectedId !== "all" && !arr.some((e) => e.id === selectedId)) {
        // If the persisted selection no longer exists, fall back to "all".
        setSelectedId("all");
        try { localStorage.setItem(STORE_KEY, "all"); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [selectedId, partner]);

  useEffect(() => { if (partner) load(); }, [partner]); // eslint-disable-line react-hooks/exhaustive-deps

  const select = useCallback((id) => {
    setSelectedId(id);
    try { localStorage.setItem(STORE_KEY, id); } catch { /* ignore */ }
  }, []);

  // Append the `est` scope to an API path (handles existing query strings).
  const scope = useCallback((path) => {
    if (!selectedId || selectedId === "all") return path;
    const sep = path.includes("?") ? "&" : "?";
    return `${path}${sep}est=${encodeURIComponent(selectedId)}`;
  }, [selectedId]);

  const selected = establishments.find((e) => e.id === selectedId) || null;

  return (
    <EstablishmentContext.Provider
      value={{ establishments, selectedId, selected, loading, load, select, scope, isAll: selectedId === "all" }}
    >
      {children}
    </EstablishmentContext.Provider>
  );
}

export function useEstablishment() {
  const c = useContext(EstablishmentContext);
  if (!c) throw new Error("EstablishmentProvider missing");
  return c;
}
